'use client';

import H2 from '@/components/typography/h2';
import Label from '@/components/typography/label';
import P from '@/components/typography/p';
import CaseItem from '@/components/ui/case-item';
import { Case, Media } from '@/payload-types';
import { Dot } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';

interface CaseGridProps {
    cases: Case[];
}

export default function CaseGrid({ cases }: CaseGridProps) {
    return (
        <div className="border-primary flex flex-col items-center justify-center gap-5 border-b sm:px-2 lg:px-6 lg:py-[1.25rem]">
            <div className="hidden w-full grid-cols-2 gap-[19px] lg:grid xl:grid-cols-3">
                {[...cases]
                    .sort((a, b) =>
                        (a._order ?? '').localeCompare(b._order ?? ''),
                    )
                    .map((caseItem) => {
                        const media = caseItem.banner as Media;
                        const imageUrl = media?.url || '/placeholder.svg';

                        return (
                            <Link
                                key={caseItem.id}
                                href={`/cases/${caseItem.slug}`}
                                className="w-full rounded-lg outline-none focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-2"
                            >
                                <div
                                    key={caseItem.id}
                                    className="group relative aspect-[4/3] h-auto w-full overflow-hidden rounded-lg transition-all duration-300"
                                >
                                    <div
                                        className="absolute inset-0 z-10 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                                        style={{
                                            backgroundColor: caseItem.color,
                                        }}
                                    >
                                        <div className="flex h-full w-full flex-col items-center justify-between pt-6 pb-2">
                                            <div className="border-primary flex w-full justify-center border-b px-4 py-6">
                                                <P
                                                    variant="large"
                                                    className="text-center"
                                                >
                                                    {
                                                        caseItem
                                                            .algemeneInformatie
                                                            .klant
                                                    }
                                                </P>
                                            </div>
                                            <H2
                                                variant="italic"
                                                className="px-4 text-center"
                                            >
                                                {caseItem.titel.toUpperCase()}
                                            </H2>
                                            <div className="border-primary flex w-full justify-center gap-5 border-t py-4">
                                                {caseItem.algemeneInformatie.diensten.map(
                                                    (item) => (
                                                        <div
                                                            className="flex items-center gap-1.5 px-4"
                                                            key={item.id}
                                                        >
                                                            <Dot
                                                                className="h-6 w-6 scale-[3]"
                                                                size={1}
                                                                strokeWidth={2}
                                                            />
                                                            <Label>
                                                                {typeof item.dienst ===
                                                                    'object' &&
                                                                'naam' in
                                                                    item.dienst
                                                                    ? item
                                                                          .dienst
                                                                          .naam
                                                                    : item.dienst}
                                                            </Label>
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {imageUrl && (
                                        <Image
                                            src={imageUrl}
                                            alt={media?.alt || ''}
                                            fill
                                            className="bg-accent object-cover transition-opacity duration-300 group-hover:opacity-0"
                                        />
                                    )}
                                </div>
                            </Link>
                        );
                    })}
            </div>
            <div className="w-full">
                <div className="grid w-full grid-cols-1 sm:grid-cols-2 lg:hidden">
                    {[...cases]
                        .sort((a, b) =>
                            (a._order ?? '').localeCompare(b._order ?? ''),
                        )
                        .map((caseItem, index) => {
                            return (
                                <>
                                    <div
                                        key={caseItem.id}
                                        className="border-primary bg-beige sticky top-[53px] flex w-full items-center justify-center border-t sm:hidden lg:top-[76px]"
                                        style={{
                                            transform: 'translateZ(0)',
                                            willChange: 'transform',
                                        }}
                                    >
                                        <CaseItem
                                            item={caseItem}
                                            index={index}
                                            isOverview
                                        />
                                    </div>
                                    <div className="hidden sm:block">
                                        <CaseItem
                                            item={caseItem}
                                            index={index}
                                            isOverview
                                        />
                                    </div>
                                </>
                            );
                        })}
                </div>
            </div>
        </div>
    );
}
